import { Box, Code, Flex, Group, Overlay, Stack, Text } from '@mantine/core';
import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { useEffect, useRef, useState } from 'react';

import { API_ENDPOINT } from '@/config/endpoint';
import ConfirmationPopup from '@/components/ConfirmationPopup';
import DMText from '@/components/DMText';
import Toast from '@/components/Toast';
import { createDiscoveryQuiz } from '@/features/Discovery/discovery.style';
import { isSignedIn } from '@/config/globalStateControl';
import { mixpanelTrack } from '@/utils/mixpanel';
import themeVariables from '@/styles/themeVariables';
import { useAtomValue } from 'jotai';
import { useDisclosure } from '@mantine/hooks';
import { useGetDiscussionQuizDataInDiscovery } from '../hook/api';

const QuizBox = ({ id }) => {
  const { classes } = createDiscoveryQuiz();

  const [opened, { toggle }] = useDisclosure(false);

  const [timing, setTiming] = useState(false);
  const [select, setSelect] = useState({ code: '', id: 0 });
  const [showAnswer, setShowAnswer] = useState(false);
  const [alreadySolved, setAlreadySolved] = useState(false);

  const mount = useRef(true);
  const signIn = useAtomValue(isSignedIn);

  const { data: quizData, refetch: refetchQuizData } =
    useGetDiscussionQuizDataInDiscovery(id, mount, setAlreadySolved);

  useEffect(() => {
    setShowAnswer(false);
    setSelect({ code: '', id: 0 });
  }, [id]);

  const submitAnswer = (option) => () => setSelect(option);

  const checkAnswer = () => {
    if (!signIn) {
      ConfirmationPopup.login('로그인 후 투표에 참여해보세요');
      return;
    }

    if (!quizData?.item?.my_vote_option_id && select.id === 0) {
      Toast.info('답변을 선택해주세요.');
      return;
    }

    if (!quizData?.item?.my_vote_option_id) {
      ConfirmationPopup.confirm(
        <Stack spacing={4}>
          <Text
            p={4}
            size={16}
            weight={600}
            color={themeVariables.fontColors.medium}
            data-autofocus
          >
            옵션을 선택하시겠습니까?
          </Text>
          <Box p={8}>
            <Code mr={2}>{select?.code}</Code>
          </Box>
        </Stack>,
        () => {
          API_ENDPOINT.discussion
            .voteOption(quizData?.item.id, select.id)
            .then(() => {
              refetchQuizData();
              mixpanelTrack('DiscoveryDetailModal_VoteOption', 'Clicked', {
                id: quizData?.item.id,
                option_id: select.id,
              });
              setShowAnswer(true);
              setTimeout(() => {
                setTiming(true);
              }, 330);
            });
        }
      );
      return;
    }

    toggle();
    setShowAnswer(true);
    setTimeout(() => {
      setTiming(true);
    }, 330);
  };

  if (!quizData?.item?.id) return null;

  return (
    <Stack my={60} spacing={0}>
      <DMText
        className={classes.title}
        mb={24}
        dColor={themeVariables.darkMode.normal}
      >
        <Text span mr={8} color="red">
          *
        </Text>
        {quizData?.item.answer_interpretation &&
        Number(quizData?.item.answer_interpretation) < 50
          ? `정답률이 ${quizData?.item.answer_interpretation}% 미만인 문제에요.`
          : `문제를 풀어보세요.`}
      </DMText>
      <Group
        bg={'#282A36'}
        p={24}
        sx={{ borderRadius: 10 }}
        spacing={10}
        noWrap
      >
        <Text size={16} weight={500} color={themeVariables.fontColors.white}>
          Q.
        </Text>
        <Text px={4} size={14} color={themeVariables.fontColors.white}>
          {quizData?.item?.content}
        </Text>
      </Group>
      <Box pos={'relative'}>
        {alreadySolved && !opened && (
          <Overlay bg={'#ffffff00'} blur={1} center h={'calc(100% - 50px)'}>
            <Text size={14} weight={500}>
              이전에 이미 응답한 기록이 있어요!
            </Text>
          </Overlay>
        )}
        <Stack spacing={12} mt={24}>
          {quizData?.item?.options?.map((option, index) => {
            return !quizData?.item?.my_vote_option_id ? (
              <Box
                key={index}
                className={classes.optionCard}
                onClick={submitAnswer(option)}
                sx={{
                  border:
                    option.id === select.id
                      ? `1px solid ${themeVariables.colors.blue300}`
                      : `1px solid ${themeVariables.colors.light200}`,
                }}
              >
                <Flex gap={10}>
                  <DMText
                    className={classes.optionCardText}
                    dColor={themeVariables.darkMode.neutral}
                  >
                    {index + 1}.
                  </DMText>
                  <DMText
                    className={classes.optionCardText}
                    dColor={themeVariables.darkMode.normal}
                  >
                    {option?.code}
                  </DMText>
                </Flex>
              </Box>
            ) : (
              <Flex
                key={index}
                className={classes.optionAnswerCard}
                sx={{
                  border: !showAnswer
                    ? `1px solid ${themeVariables.colors.light200}`
                    : quizData?.item?.answer_option_id ===
                        quizData?.item?.my_vote_option_id &&
                      option.id === quizData?.item?.answer_option_id
                    ? `1px solid ${themeVariables.colors.blue300}`
                    : quizData?.item?.answer_option_id !==
                        quizData?.item?.my_vote_option_id &&
                      option.id === quizData?.item?.my_vote_option_id
                    ? `1px solid ${themeVariables.colors.red200}`
                    : `1px solid ${themeVariables.colors.light200}`,
                  padding: '12px 20px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: !showAnswer
                      ? themeVariables.colors.white
                      : option.id === quizData.item.answer_option_id
                      ? themeVariables.colors.blue100
                      : themeVariables.colors.light100,
                    width: !showAnswer
                      ? 'auto'
                      : option.percent && showAnswer && timing
                      ? `calc(${option.percent}% + 12px)`
                      : '0%',
                    transition: option.percent ? 'width 0.33s linear' : '',
                    position: 'absolute',
                    height: 'calc(100% + 20px)',
                    top: -20,
                    left: 0,
                    zIndex: -1,
                  }}
                />
                <Flex align={'center'} gap={5}>
                  <Flex gap={10}>
                    <DMText
                      className={classes.optionCardText}
                      dColor={themeVariables.darkMode.normal}
                    >
                      {index + 1}.
                    </DMText>
                    <DMText
                      className={classes.optionCardText}
                      dColor={themeVariables.darkMode.normal}
                    >
                      {option?.code}
                    </DMText>
                  </Flex>
                  {showAnswer && (
                    <Text
                      size={12}
                      weight={500}
                      color={themeVariables.label.alternative}
                      lh={'16px'}
                      mt={2}
                    >
                      ({option.percent}%)
                    </Text>
                  )}
                  {option.id === quizData.item.answer_option_id &&
                    showAnswer && (
                      <Box
                        p={'3px 4px'}
                        bg={themeVariables.colors.blue200}
                        sx={{
                          borderRadius: 4,
                        }}
                      >
                        <Text
                          size={11}
                          weight={600}
                          color={themeVariables.colors.white}
                          lh={'14px'}
                        >
                          정답
                        </Text>
                      </Box>
                    )}
                </Flex>
              </Flex>
            );
          })}
        </Stack>
        <Flex gap={10} mt={24} align={'flex-start'}>
          {quizData?.item?.my_vote_option_id && showAnswer && (
            <Text color={themeVariables.fontColors.light} size={13} lh={'18px'}>
              ☝🏻 {quizData?.item.answer_interpretation}
            </Text>
          )}
          <Button
            type={ButtonTypes.solidColored}
            p="9px 20px"
            ml="auto"
            onClick={checkAnswer}
            sx={{
              backgroundColor:
                quizData?.item?.my_vote_option_id && showAnswer
                  ? themeVariables.label.disable
                  : themeVariables.colors.blue200,
              color:
                quizData?.item?.my_vote_option_id && showAnswer
                  ? themeVariables.label.assistive
                  : themeVariables.colors.white,
            }}
            disabled={showAnswer ? true : false}
          >
            {quizData?.item?.my_vote_option_id ? '정답 확인하기' : '확인하기'}
          </Button>
        </Flex>
      </Box>
    </Stack>
  );
};

export default QuizBox;
